<template>
  <div class="import">
    <div class="page-header">
      <h2>数据导入</h2>
      <p>导入Excel数据到系统</p>
    </div>
    
    <div class="page-content">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card>
            <div slot="header">
              <span>本地Excel导入</span>
            </div>
            <el-button type="primary" @click="openUploadDialog">上传Excel</el-button>
            <p style="margin-top: 10px; color: #909399;">请按照模版准备数据后上传。</p>
          </el-card>
        </el-col>
        
        <el-col :span="12">
          <el-card>
            <div slot="header">
              <span>服务器Excel导入</span>
            </div>
            <el-form :model="importForm" label-width="100px">
              <el-form-item label="文件路径：">
                <el-input v-model="importForm.filePath" placeholder="请输入服务器文件路径"></el-input>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="importServerFile" :loading="importLoading">导入文件</el-button>
              </el-form-item>
            </el-form>
          </el-card>
        </el-col>
      </el-row>
      
      <!-- 导入进度 -->
      <el-card v-if="importProgress.show" style="margin-top: 20px;">
        <div slot="header">
          <span>导入进度</span>
        </div>
        <div class="progress-container">
          <el-progress 
            :percentage="importProgress.percentage" 
            :status="importProgress.status"
            :stroke-width="8">
          </el-progress>
          <div class="progress-text">
            <p><strong>当前步骤：</strong>{{ importProgress.currentStep }}</p>
            <p><strong>进度：</strong>{{ importProgress.percentage }}%</p>
            <p v-if="importProgress.details"><strong>详情：</strong>{{ importProgress.details }}</p>
          </div>
        </div>
      </el-card>
      
      <!-- 导入结果 -->
      <el-card v-if="importResult" style="margin-top: 20px;">
        <div slot="header">
          <span>导入结果</span>
        </div>
        <el-alert
          :title="importResult.message"
          :type="importResult.success ? 'success' : 'error'"
          :description="importResult.details"
          show-icon>
        </el-alert>
      </el-card>
    </div>
    <el-dialog
      title="上传Excel"
      :visible.sync="uploadDialogVisible"
      width="520px"
      @close="handleUploadDialogClose">
      <el-alert
        type="info"
        show-icon
        title="步骤提醒"
        description="先填写主包信息，下载模版.xlsx，并在模版中准备数据后上传。"
        style="margin-bottom: 16px;" />
      <el-form
        ref="packageFormRef"
        :model="packageForm"
        :rules="packageRules"
        label-width="100px">
        <el-form-item label="主包名称" prop="packageName">
          <el-select
            v-model="packageForm.packageName"
            filterable
            allow-create
            default-first-option
            placeholder="请选择或输入主包名称"
            :loading="packageNameLoading"
            @focus="loadPackageNames"
            @change="handlePackageNameChange"
            style="width: 100%;">
            <el-option
              v-for="item in packageNameList"
              :key="item.id"
              :label="item.package_name"
              :value="item.package_name">
              <span style="float: left">{{ item.package_name }}</span>
              <span style="float: right; color: #8492a6; font-size: 13px">{{ item.percentage }}%</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="百分比(%)" prop="percentage">
          <el-input-number
            v-model="packageForm.percentage"
            :min="0"
            :max="1000"
            :precision="2"
            style="width: 100%;"
            placeholder="请输入百分比">
          </el-input-number>
        </el-form-item>
        <el-form-item label="模板下载">
          <el-link :href="templateUrl" target="_blank" type="primary">下载模版.xlsx</el-link>
        </el-form-item>
        <el-form-item label="选择文件">
          <el-upload
            ref="localUpload"
            :file-list="fileList"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            :before-upload="beforeUpload"
            :auto-upload="false"
            accept=".xlsx,.xls">
            <el-button size="small" type="primary">选择文件</el-button>
            <div slot="tip" class="el-upload__tip">仅支持 .xlsx/.xls，文件小于10MB</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="uploadDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="importLoading" @click="submitUpload">开始上传</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'Import',
  data() {
    return {
      uploadUrl: '/import/excel',
      fileList: [],
      importLoading: false,
      uploadDialogVisible: false,
      templateUrl: '/模版.xlsx',
      packageForm: {
        packageName: '',
        percentage: null
      },
      packageRules: {
        packageName: [{ required: true, message: '请输入主包名称', trigger: 'blur' }],
        percentage: [{ required: true, message: '请输入百分比', trigger: 'change' }]
      },
      importForm: {
        filePath: ''
      },
      importResult: null,
      importProgress: {
        show: false,
        percentage: 0,
        status: '',
        currentStep: '',
        details: ''
      },
      sessionId: '',
      progressEventSource: null,
      packageNameList: [],
      packageNameLoading: false
    }
  },
  beforeDestroy() {
    // 清理进度流连接
    if (this.progressEventSource) {
      this.progressEventSource.close()
      this.progressEventSource = null
    }
  },
  methods: {
    // 生成会话ID
    generateSessionId() {
      return 'import_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    },

    openUploadDialog() {
      this.resetUploadDialog()
      this.uploadDialogVisible = true
      this.loadPackageNames()
    },
    
    async loadPackageNames() {
      if (this.packageNameList.length > 0) {
        // 如果已经加载过，不再重复加载
        return
      }
      
      this.packageNameLoading = true
      try {
        const response = await this.$http.get('/import/packages/names')
        const data = response.data.data || response.data
        this.packageNameList = data.items || []
      } catch (error) {
        console.error('加载主包名称列表失败:', error)
        this.$message.warning('加载主包列表失败，可以手动输入主包名称')
      } finally {
        this.packageNameLoading = false
      }
    },
    
    handlePackageNameChange(value) {
      // 如果选择的是已有主包，自动填充百分比
      const selectedPackage = this.packageNameList.find(item => item.package_name === value)
      if (selectedPackage) {
        this.packageForm.percentage = selectedPackage.percentage
      }
    },

    handleUploadDialogClose() {
      if (this.$refs.packageFormRef) {
        this.$refs.packageFormRef.resetFields()
      }
      this.fileList = []
    },

    resetUploadDialog() {
      this.packageForm = {
        packageName: '',
        percentage: null
      }
      if (this.$refs.packageFormRef) {
        this.$refs.packageFormRef.clearValidate()
      }
      this.fileList = []
    },
    
    // 连接进度流
    connectProgressStream() {
      if (this.progressEventSource) {
        this.progressEventSource.close()
      }
      
      console.log('连接进度流，sessionId:', this.sessionId)
      this.progressEventSource = new EventSource(`/import/progress?session_id=${this.sessionId}`)
      
      this.progressEventSource.onopen = () => {
        console.log('进度流连接已建立')
      }
      
      this.progressEventSource.onmessage = (event) => {
        console.log('收到进度数据:', event.data)
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'heartbeat') {
            console.log('收到心跳')
            return
          }
          
          console.log('更新进度:', data)
          this.importProgress.percentage = data.percentage
          this.importProgress.currentStep = data.step
          this.importProgress.details = data.details
          
          if (data.percentage >= 100) {
            this.importProgress.status = 'success'
            this.importProgress.currentStep = '导入完成'
            this.importProgress.details = '所有数据已成功保存到数据库'
            
            // 关闭连接
            this.progressEventSource.close()
            this.progressEventSource = null
          }
        } catch (error) {
          console.error('解析进度数据失败:', error, '原始数据:', event.data)
        }
      }
      
      this.progressEventSource.onerror = (error) => {
        console.error('进度流连接错误:', error)
        this.importProgress.status = 'exception'
        this.importProgress.currentStep = '连接错误'
        this.importProgress.details = '无法获取实时进度'
      }
    },
    
    handleFileChange(file, fileList) {
      console.log('handleFileChange file:', file)
      console.log('handleFileChange fileList:', fileList)
      this.fileList = fileList
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList
    },
    
    beforeUpload(file) {
      console.log('beforeUpload file:', file)
      
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.type === 'application/vnd.ms-excel'
      if (!isExcel) {
        this.$message.error('只能上传Excel文件!')
        return false
      }
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        this.$message.error('文件大小不能超过10MB!')
        return false
      }
      
      // 只做文件验证，不连接进度流
      return true
    },
    
    submitUpload() {
      this.$refs.packageFormRef.validate((valid) => {
        if (!valid) {
          return
        }

        if (!this.fileList || this.fileList.length === 0) {
          this.$message.error('请先选择Excel文件')
          return
        }

        this.sessionId = this.generateSessionId()
        this.connectProgressStream()

        this.importProgress = {
          show: true,
          percentage: 0,
          status: '',
          currentStep: '准备上传文件...',
          details: ''
        }
        this.importResult = null

        this.importLoading = true
        this.importProgress.currentStep = '开始上传文件...'
        this.importProgress.percentage = 10

        let file = null
        if (this.fileList[0].raw) {
          file = this.fileList[0].raw
        } else if (this.fileList[0].originFileObj) {
          file = this.fileList[0].originFileObj
        } else if (this.fileList[0] instanceof File) {
          file = this.fileList[0]
        } else if (this.fileList[0].file) {
          file = this.fileList[0].file
        }

        if (!file) {
          this.$message.error('文件对象无效，请重新选择文件')
          this.importLoading = false
          return
        }

        const formData = new FormData()
        formData.append('file', file)
        formData.append('session_id', this.sessionId)
        formData.append('package_name', this.packageForm.packageName)
        formData.append('percentage', this.packageForm.percentage)

        this.$http.post('/import/excel', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const uploadProgress = Math.round((progressEvent.loaded / progressEvent.total) * 90)
            this.importProgress.percentage = uploadProgress
            this.importProgress.currentStep = '正在上传文件...'
            this.importProgress.details = `已上传 ${Math.round(progressEvent.loaded / 1024)} KB / ${Math.round(progressEvent.total / 1024)} KB`
          }
        }).then(response => {
          this.handleUploadSuccess(response.data, null, this.fileList)
        }).catch(error => {
          this.handleUploadError(error, null, this.fileList)
        })
      })
    },
    
    handleUploadProgress(event, file, fileList) {
      // 上传进度（0-90%）
      const uploadProgress = Math.round((event.loaded / event.total) * 90)
      this.importProgress.percentage = uploadProgress
      this.importProgress.currentStep = '正在上传文件...'
      this.importProgress.details = `已上传 ${Math.round(event.loaded / 1024)} KB / ${Math.round(event.total / 1024)} KB`
    },
    
    
    handleUploadSuccess(response, file, fileList) {
      console.log('上传成功响应:', response)
      this.importLoading = false
      this.uploadDialogVisible = false
      this.handleUploadDialogClose()
      this.fileList = fileList
      
      // 更新进度到100%
      this.importProgress.percentage = 100
      this.importProgress.status = 'success'
      this.importProgress.currentStep = '导入完成'
      this.importProgress.details = '文件已成功上传并处理'
      
      // 处理响应数据
      const data = response.data || response
      console.log('处理后的数据:', data)
      
      this.importResult = {
        success: data.success !== false,
        message: data.message || '导入成功',
        details: data.details || '文件已成功导入到系统'
      }
      
      this.$message.success(this.importResult.message)
      
      // 3秒后隐藏进度条
      setTimeout(() => {
        this.importProgress.show = false
      }, 3000)
    },
    
    handleUploadError(err, file, fileList) {
      this.importLoading = false
      this.fileList = fileList
      
      // 更新进度状态为错误
      this.importProgress.status = 'exception'
      this.importProgress.currentStep = '上传失败'
      this.importProgress.details = err.message || '上传过程中发生错误'
      
      this.importResult = {
        success: false,
        message: '导入失败',
        details: err.message || '上传过程中发生错误'
      }
      
      this.$message.error('文件上传失败!')
    },
    
    async importServerFile() {
      if (!this.importForm.filePath) {
        this.$message.warning('请输入文件路径')
        return
      }
      
      // 生成会话ID并连接进度流
      this.sessionId = this.generateSessionId()
      this.connectProgressStream()
      
      // 重置进度状态
      this.importProgress = {
        show: true,
        percentage: 0,
        status: '',
        currentStep: '准备导入服务器文件...',
        details: ''
      }
      this.importResult = null
      
      this.importLoading = true
      
      try {
        const response = await this.$http.post('/import/local-excel', {
          file_path: this.importForm.filePath,
          session_id: this.sessionId
        })
        
        console.log('服务器导入成功响应:', response)
        const data = response.data.data || response.data
        console.log('处理后的数据:', data)
        
        this.importResult = {
          success: data.success !== false,
          message: data.message || '导入成功',
          details: data.details || '文件已成功导入到系统'
        }
        
        this.$message.success(this.importResult.message)
        
        // 3秒后隐藏进度条
        setTimeout(() => {
          this.importProgress.show = false
        }, 3000)
        
      } catch (error) {
        this.importProgress.status = 'exception'
        this.importProgress.currentStep = '导入失败'
        this.importProgress.details = (error.response && error.response.data && error.response.data.message) || error.message || '导入过程中发生错误'
        
        this.importResult = {
          success: false,
          message: '导入失败',
          details: (error.response && error.response.data && error.response.data.message) || error.message || '导入过程中发生错误'
        }
        
        this.$message.error('文件导入失败!')
      } finally {
        this.importLoading = false
      }
    },
    
    updateProgress(percentage, step, details) {
      this.importProgress.percentage = percentage
      this.importProgress.currentStep = step
      this.importProgress.details = details
    },
    
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    }
  }
}
</script>

<style scoped>
.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 10px 0;
  color: #303133;
}

.page-header p {
  margin: 0;
  color: #909399;
}

.progress-container {
  padding: 20px 0;
}

.progress-text {
  margin-top: 15px;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
  border-left: 4px solid #409eff;
}

.progress-text p {
  margin: 5px 0;
  color: #606266;
  font-size: 14px;
}

.progress-text p strong {
  color: #303133;
}
</style>
